<template>
  <div>
    <stop-card
      v-for="key in sortedReceiptGroupKeys"
      :key="'receipt-group-' + key"
      icon="mdi-receipt-text-outline"
      :title="getReceiptTitle(groupedReceipts[key])"
      :subtitles="displayInventories[key].map(buildInventoryDisplayString)"
      @click="onClick(groupedReceipts[key])"
      state="completed"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { groupBy, sortBy } from 'lodash'
import {
  ActualStop,
  type InventoryDisplayItem,
  InventoryReceipt,
  InventoryReceiptType,
  JobStopType,
} from '@ironsight/types'
import { useStore } from 'vuex-composition-helpers'
import { buildDisplayInventory, buildInventoryDisplayString } from '@/utils/inventories'
import { useInventoryTypes } from '@/composables/inventory-types/useInventoryTypes'
import StopCard from '@/components/tickets/StopsAndInventories/StopCard.vue'

const props = defineProps<{
  stop: ActualStop
}>()

const emit = defineEmits<{
  (e: 'click', receipts: InventoryReceipt[], receiptType: InventoryReceiptType): void
}>()

const onClick = (receipts: InventoryReceipt[]): void => {
  emit('click', receipts, receipts[0].receiptType)
}

const store = useStore()

const getUserName = (receipt: InventoryReceipt): string => {
  const sid = receipt.createdBy
  const user = store.getters['users/getBySid'](sid)
  return user?.name ?? 'Unknown'
}

const getReceiptType = (receipt: InventoryReceipt): string => {
  if (props.stop.stopType === JobStopType.PickUp) {
    return receipt.receiptType === InventoryReceiptType.Originator ? 'Transfer' : 'Pickup'
  } else {
    return receipt.receiptType === InventoryReceiptType.Originator ? 'Dropoff' : 'Receipt'
  }
}

const getReceiptTime = (receipt: InventoryReceipt): string => {
  const type = getReceiptType(receipt)
  if (type === 'Pickup' || type === 'Dropoff') {
    return props.stop.arrivedAt.format('MMM D, YYYY HH:mm')
  } else {
    return receipt.createdAt.format('MMM D, YYYY HH:mm')
  }
}

const getReceiptTitle = (receipts: InventoryReceipt[]) => {
  const receipt = receipts[0]
  return `${getReceiptType(receipt)} Confirmed by ${getUserName(receipt)} ${getReceiptTime(
    receipt,
  )}`
}

const groupedReceipts = computed(() =>
  groupBy(props.stop?.inventoryReceipts, (r) => `${r.receiptType}-${r.ownerOrgId}`),
)
const sortedReceiptGroupKeys = computed(() => sortBy(Object.keys(groupedReceipts.value)))

const { allInventoryTypes } = useInventoryTypes()

const displayInventories = computed(() => {
  const displayInventories: Record<string, InventoryDisplayItem[]> = {}
  for (const [key, receipts] of Object.entries(groupedReceipts.value)) {
    displayInventories[key] = buildDisplayInventory(receipts, allInventoryTypes.value ?? [])
  }
  return displayInventories
})
</script>
